import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';

import { useAppContext } from '../AppContext';

const CreateMomentForm = () => {
    const { t } = useTranslation();
    const { userId, appFunctions, setMessage, handlePageChange, uploadFile, currentUserProfile, addOptimisticMoment, removeOptimisticMoment } = useAppContext();
    const [mediaFile, setMediaFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [content, setContent] = useState('');
    const [tags, setTags] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const mediaInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('video/')) {
            if (file.size > 50 * 1024 * 1024) { // 50MB limit
                setMessage(t('createMomentForm.fileSizeError'));
                return;
            }
            setMediaFile(file);
            // Revoke previous URL to prevent memory leaks
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
            setPreviewUrl(URL.createObjectURL(file));
        } else {
            setMessage(t('createMomentForm.invalidFileError'));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!mediaFile) {
            setMessage(t('createMomentForm.noFileError'));
            return;
        }
        setIsSubmitting(true);
        setUploadProgress(0);

        // --- OPTIMISTIC UPDATE START ---
        const tempId = `optimistic_${Date.now()}`;
        const optimisticMoment = {
            id: tempId,
            authorId: userId,
            authorName: currentUserProfile?.displayName || 'You',
            authorPhotoURL: currentUserProfile?.photoURL,
            content: content.trim(),
            tags: tags.split(',').map(t => t.trim()).filter(Boolean),
            mediaUrl: previewUrl, // Use the local blob URL for instant preview
            moderationStatus: 'uploading', // Special status for optimistic UI
            timestamp: new Date(),
            likesCount: 0,
            commentsCount: 0,
            echoesInvested: 0,
            isOptimistic: true, // Flag to identify this as a temporary item
        };

        addOptimisticMoment(optimisticMoment);
        handlePageChange('myMoments');
        // --- OPTIMISTIC UPDATE END ---

        try {
            const createMomentShell = httpsCallable(appFunctions, 'createMoment');
            const result = await createMomentShell({
                content: content.trim(),
                tags: tags.split(',').map(t => t.trim()).filter(Boolean),
            });

            const { momentId } = result.data;
            if (!momentId) {
                throw new Error(t('createMomentForm.noMomentIdError'));
            }

            const filePath = `moments/${userId}/${momentId}_${mediaFile.name}`;
            await uploadFile(mediaFile, filePath, (progress) => {
                setUploadProgress(progress); // This won't be visible as we navigate away
            });

            const processMoment = httpsCallable(appFunctions, 'processMoment');
            await processMoment({ momentId, filePath });

            // The real moment will now appear via the Firestore listener.
            // We can remove the optimistic one.
            removeOptimisticMoment(tempId);
            // No success message needed, as the user sees the result instantly.

        } catch (error) {
            console.error("Error creating Moment:", error);
            setMessage(t('createMomentForm.postError', { error: error.message }));
            // If something fails, remove the optimistic moment to avoid a broken UI state
            removeOptimisticMoment(tempId);
        } finally {
            setIsSubmitting(false);
            // Clean up the blob URL after we're done with it
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        }
    };

    return (
        <div className="moment-creation-container">
            <h2 className="text-3xl font-bold text-center mb-6 text-purple-300 font-playfair">{t('createMomentForm.title')}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="text-center text-gray-300 mb-4">
                    <p>{t('createMomentForm.description')}</p>
                </div>
                <div className="flex items-center justify-center">
                    <button type="button" onClick={() => mediaInputRef.current.click()} className="small-action-button bg-indigo-600 hover:bg-indigo-700 text-white text-base px-6 py-3">
                        <LucideIcons.UploadCloud size={20} className="mr-2" /> {t('createMomentForm.selectVideo')}
                    </button>
                    <input type="file" ref={mediaInputRef} onChange={handleFileSelect} className="hidden" accept="video/*" />
                </div>

                {isSubmitting && uploadProgress > 0 && <progress value={uploadProgress} max="100" className="w-full accent-purple-500" />}

                {previewUrl && (
                    <div className="moment-preview-container">
                        <video
                            src={previewUrl}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="moment-preview-video"
                        ></video>
                    </div>
                )}

                <textarea className="moment-creation-input w-full p-3 rounded-lg resize-y" placeholder={t('createMomentForm.captionPlaceholder')} value={content} onChange={(e) => setContent(e.target.value)} rows="3" />
                <input type="text" className="moment-creation-input w-full py-2 px-4 rounded-full" placeholder={t('createMomentForm.tagsPlaceholder')} value={tags} onChange={(e) => setTags(e.target.value)} />

                <button type="submit" disabled={isSubmitting || !mediaFile} className="w-full px-8 py-3 bg-purple-600 text-white font-bold rounded-full hover:bg-purple-700 transition duration-300 disabled:opacity-50 flex items-center justify-center gap-2">
                    {isSubmitting ? <div className="action-spinner" /> : <LucideIcons.Send size={18} />}
                    {isSubmitting ? t('createMomentForm.uploading') : t('createMomentForm.postMoment')}
                </button>
            </form>
        </div>
    );
}

export default CreateMomentForm;

